(ns ycweb.app.admin.wholesale.stockorders.common
  (:require
   #?@(:clj
       [[db.all :as db]
        [macaron.mysql :as mysql]])
   [util.string :as str]
   [util.transform :as trf]
   [ycweb.components.common :as cmn]
   [ycweb.util.core :as yuc]))

(def dev-permission
  "SiteAdmin:Teams:Development")

(def view-permission
  "SiteAdmin:Wholesale:StockOrder:View")

(def api-permissions
  #{dev-permission view-permission "SiteAdmin:Teams:Finance"})

(def view-permission-set
  #{dev-permission view-permission})

(def edit-permission
  "SiteAdmin:Wholesale:StockOrder:Edit")

(def edit-permission-set
  #{dev-permission edit-permission})

(def edit-invoices
  "SiteAdmin:Wholesale:StockOrder:Edit:Invoices")

(def edit-credit-notes
  "SiteAdmin:Wholesale:StockOrder:Edit:CreditNotes")

(def edit-manual-journals
  "SiteAdmin:Wholesale:StockOrder:Edit:ManualJournals")

(defn validate-undo [stockorder]
  (let [status
        (or
          (:stockorder/status stockorder)
          (:status stockorder))]
    (cond
      (= status "Draft")
      {:success false
       :msg (str "Stock Order status is already " status)}

      (not= status "Placed")
      {:success false
       :msg
       (str "Cannot perform undo on Stock Order, with status " status)}

      :else
      {:success true})))

(defn line-item-is-stock? [line-item]
  (= (:stockorderitem/type line-item) "Stock"))

(defn calculate-receiving-status [action-atom]
  (let [stockorder (cmn/curact action-atom :stockorder)
        pre-received? (:stockorder/dateprereceived stockorder)
        received? (= (:stockorder/status stockorder) "Received")
        partial-received? (:receivingsessions @action-atom)]

    (cond
      received? "Received"
      partial-received? "Partially Received"
      pre-received? "Pre-Received"
      :else "Not Received")))

(defn shipping-address [{:keys [address suburb city provincecode postalcode]}]
  (let [shipping-display-line-1
        (str/trim (when (yuc/presence address) address))
        [line1 line2] (str/split shipping-display-line-1 #",")
        shipping-display-line-2
        (str/trim
          (str
            (when (yuc/presence suburb) (str suburb ", "))
            (when (yuc/presence city) (str city ", "))
            (when (yuc/presence provincecode) (str provincecode ", "))
            (when (yuc/presence postalcode) postalcode)))]
    {:line-1 (str/trim line1)
     :line-2 (str/trim line2)
     :shipping-display-line-1 shipping-display-line-1
     :shipping-display-line-2 shipping-display-line-2}))

(defn billing-address [{:keys [line1 line2 city province postcode country]}]
  (let [billing-display-line-1
        (str line1 (when (yuc/presence line2) (str ", " line2)))
        billing-display-line-2
        (str/trim
          (str
            (when (yuc/presence city) (str city ", "))
            (when (yuc/presence province) (str province ", "))
            (when (yuc/presence postcode) (str postcode ", "))
            (when (yuc/presence country) country)))]
    {:billing-display-line-1 billing-display-line-1
     :billing-display-line-2 billing-display-line-2}))

(defn transform-stock-order
  [stock-order {:keys [supplier warehouse currency supplier-address dear-entity]}]
  (let [{:keys [shipping-display-line-1 shipping-display-line-2]}
        (shipping-address warehouse)
        {:keys [billing-display-line-2]}
        (billing-address supplier-address)]
    (->
      (yuc/maybe assoc
        stock-order
        :import_order? (trf/toboolean (yuc/presence (:import_order_id stock-order)))
        :supplier/name (:name supplier)
        :currency/code (:unicode currency)
        :dear/date-modified (:datemodified dear-entity)
        :stockorder/display-line-2 (yuc/presence billing-display-line-2)
        :shipping/display-line-1 (yuc/presence shipping-display-line-1)
        :shipping/display-line-2 (yuc/presence shipping-display-line-2)
        :documents
        (when (yuc/presence (:invoice_numbers stock-order))
          (str (:invoice_numbers stock-order)
            (when (yuc/presence (:credit_note_numbers stock-order))
              (str ", " (:credit_note_numbers stock-order))))))
      (yuc/map->nsmap "stockorder"))))

(defn transform-stock-order-item
  [stock-order-item {:keys [variation brand tax-rule]}]
  (->
    stock-order-item
    (merge
      {:variation/id (:id variation)
       :variation/brand_id (:id brand)
       :variation/import_tariff_id (:import_tariff_id variation)
       :variation/name (:sku variation)
       :product/name (:name variation)
       :variation/barcode (:barcode variation)
       :variation/fullname (:fullname variation)
       :variation/lengthmm (:lengthmm variation)
       :variation/heightmm (:heightmm variation)
       :variation/widthmm (:widthmm variation)
       :variation/features (:features variation)
       :variation/delivery (:delivery variation)
       :taxrule/description (:description tax-rule)})
    (yuc/map->nsmap "stockorderitem")))

(defn stock-order-validation-rules [action opts]
  (let [edit-rules
        (when (= action :edit)
          {:stockorder/uuid [:required :uuid]
           :stockorder/supplier_contact_id
           [:nillable
            {:rule :in
             :config {:list (:supplier-contact-ids opts [])}}]
           :stockorder/supplier_address_id
           [:nillable
            {:rule :in
             :config {:list (:supplier-address-ids opts [])}}]
           :stockorder/paymentterm_id
           [:nillable
            {:rule :in
             :config {:list (:payment-term-ids opts [])}}]
           :stockorder/taxrule_id
           [:nillable
            {:rule :in
             :config {:list (:tax-rule-ids opts [])}}]
           :stockorder/dateexpected
           [:nillable
            {:rule :date}
            {:rule :after
             :config {:date :stockorder/dateadded}}]
           :stockorder/currencycode
           [:nillable :string
            {:rule :digits-between :config {:min 3 :max 5}}
            {:rule :in
             :config {:list (:currency-codes opts [])}}]})]
    (merge
      (yuc/maybe assoc
        {:stockorder/supplier_id
         [:required
          {:rule :in
           :config {:list (:supplier-ids opts [])}}]
         :stockorder/destination_id
         [:required
          {:rule :in
           :config {:list (:destination-ids opts [])}}]
         :stockorder/company_id
         [:required
          {:rule :in
           :config {:list (:company-ids opts [])}}]
         :stockorder/currencyrate
         [:required
          {:rule :gte :config {:value 1}}]})
      (or edit-rules {}))))

(defn validate-stock-order [{{:keys [ihdb]} :ctx override? :override?} row value]
  #?(:cljs
     nil
     :clj
     (let [company (mysql/find-by ihdb db/company :name "Edison Stone")
           stockorder
           (mysql/find-by-fields ihdb db/stockorder
             [:id :status]
             :id value
             :company_id (:id company))

           invoice-link (mysql/find-by ihdb db/purchase_invoice :stockorder_id (:id stockorder))
           purchase-invoice (mysql/find-by ihdb db/invoice :id (:invoice_id invoice-link))]

       (cond
         (and purchase-invoice (not (= (:status purchase-invoice) "Draft")))
         (str "The invoice status of this stock order is " (:status purchase-invoice) ". Finance must first put the invoice in Draft status before amendments can be made.")

         (and
           (not (contains? #{"Draft" "Placed"} (:status stockorder)))
           (not override?))
         (str "Products cannot be added to a stock order in status: " (:status stockorder))

         :else
         nil))))

(defn order-item-validation-rules [action opts]
  (yuc/maybe assoc
    {#_#_;;Removing until brands are linked to products
         :variation/brand_id
       [:required
        {:rule :in
         :config {:list (:brand-ids opts [])}}]
     :stockorderitem/variation_id
     [:required
      {:rule :in
       :config {:list (:variation-ids opts [])}}]
     #_#_;;Removing until brands are linked to products
         :stockorderitem/variation_id
       [:required
        {:rule :in-group
         :config
         {:list (:variation-ids-by-brand opts [])
          :group-key :variation/brand_id}}]
     :stockorderitem/type
     [:required
      {:rule :in
       :config
       {:list (:types opts [])}}]
     :stockorderitem/stockorder_id
     [:required
      {:rule :in
       :config
       {:list (:drafted-stockorder-ids opts [])}}
      {:rule :fn
       :rule-fn (partial validate-stock-order opts)}]
     :stockorderitem/qty [:nillable :integer]
     :stockorderitem/discount [:nillable :double]
     :stockorderitem/costprice [:nillable :integer]
     :stockorderitem/taxrule_id
     [:required
      {:rule :in
       :config {:list (:tax-rule-ids opts [])}}]
     :stockorderitem/comment [:nillable :string]}))

(defn order-charges-validation-rules [action opts]
  (yuc/maybe assoc
    {:stockorderitem/variation_id
     [:required
      {:rule :in
       :config {:list (:variation-ids opts [])}}]
     :stockorderitem/stockorder_id
     [:required
      {:rule :in
       :config
       {:list (:drafted-stockorder-ids opts [])}}
      {:rule :fn
       :rule-fn (partial validate-stock-order opts)}]
     :stockorderitem/qty [:nillable :integer]
     :stockorderitem/discount [:nillable :double]
     :stockorderitem/costprice [:nillable :integer]
     :stockorderitem/taxrule_id
     [:required
      {:rule :in
       :config {:list (:tax-rule-ids opts [])}}]
     :stockorderitem/comment [:nillable :string]}))

(defn order-item-validation-messages []
  ;;TODO: Hook this into the validate function. Override the messages.
  {:stockorder/id
   {:required "The stock order is missing from this submission"
    :in "Stock Order not found in the list of draft orders"}})